import React, { useState, useEffect, useCallback } from 'react';
import { Send, Mail, CheckCircle, XCircle, FileText, Plus, Edit, RefreshCw, FileEdit, MessageSquareReply, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { EmailInbox } from './EmailInbox';
import { EmailComposer } from './email/EmailComposer';
import { ReplyTemplatesPanel } from './email/ReplyTemplatesPanel';
import { DraftsList } from './email/DraftsList';
import { ScheduledList } from './email/ScheduledList';
import { useReplyTemplates } from './email/TemplatePicker';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TEMPLATE_COLORS = {
  quote_created: 'bg-blue-100 text-blue-700', sales_order_created: 'bg-purple-100 text-purple-700', sales_order_approved: 'bg-green-100 text-green-700',
  invoice_created: 'bg-amber-100 text-amber-700', invoice_paid: 'bg-emerald-100 text-emerald-700', low_stock_alert: 'bg-red-100 text-red-700',
  project_status_update: 'bg-cyan-100 text-cyan-700', chat_reply: 'bg-teal-100 text-teal-700', manual: 'bg-stone-200 text-stone-800',
};
const getTemplateColor = (name) => TEMPLATE_COLORS[name] || 'bg-neutral-100 text-neutral-700';

export const MailsTab = ({ token }) => {
  const { user } = useAuth();
  const agentName = user?.name || user?.email || 'Apollo Shades';
  const [emails, setEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [replyTemplates, , reloadReplyTemplates] = useReplyTemplates(token);
  const [drafts, setDrafts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('history');
  const [refreshKey, setRefreshKey] = useState(0);
  const [composer, setComposer] = useState({ open: false, initial: null, title: 'New message' });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', description: '', active: true });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/admin/emails?limit=300`, { headers: { Authorization: `Bearer ${token}` } }); setEmails(r.data); }
    catch { toast.error('Failed to load email history'); } finally { setLoading(false); }
  }, [token]);
  const fetchTemplates = useCallback(() => axios.get(`${API_URL}/admin/email-templates`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setTemplates(r.data.filter((t) => t.kind !== 'reply'))).catch(() => {}), [token]);
  const fetchDrafts = useCallback(() => axios.get(`${API_URL}/admin/email-drafts`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setDrafts(r.data)).catch(() => {}), [token]);
  const fetchScheduled = useCallback(() => axios.get(`${API_URL}/admin/emails/scheduled`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setScheduled(r.data)).catch(() => {}), [token]);
  useEffect(() => { fetchEmails(); fetchTemplates(); fetchDrafts(); fetchScheduled(); }, [fetchEmails, fetchTemplates, fetchDrafts, fetchScheduled]);

  const openComposer = (initial = null, title = 'New message') => setComposer({ open: true, initial, title });
  const scheduleComposed = async (payload, sendAt) => {
    const body = { kind: 'email', send_at: sendAt, payload };
    if (composer.initial?.scheduled_id) await axios.put(`${API_URL}/admin/emails/scheduled/${composer.initial.scheduled_id}`, body, { headers });
    else await axios.post(`${API_URL}/admin/emails/schedule`, body, { headers });
    if (payload.draft_id) await axios.delete(`${API_URL}/admin/email-drafts/${payload.draft_id}`, { headers }).catch(() => {});
    fetchScheduled(); fetchDrafts(); setActiveView('scheduled');
  };
  const sendNow = async (i) => {
    setBusyId(i.id);
    try { await axios.post(`${API_URL}/admin/emails/scheduled/${i.id}/send-now`, {}, { headers }); toast.success('Sent'); fetchScheduled(); fetchEmails(); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); fetchScheduled(); } finally { setBusyId(null); }
  };
  const cancelScheduled = async (i) => {
    if (!window.confirm('Cancel this scheduled message?')) return;
    try { await axios.delete(`${API_URL}/admin/emails/scheduled/${i.id}`, { headers }); fetchScheduled(); } catch { toast.error('Could not cancel'); }
  };
  const editScheduled = (i) => openComposer({ ...i.payload, scheduled_id: i.id, send_at: i.send_at }, 'Edit scheduled message');
  const sendComposed = async (payload) => {
    if (composer.initial?.scheduled_id) {
      await axios.put(`${API_URL}/admin/emails/scheduled/${composer.initial.scheduled_id}`, { kind: 'email', send_at: composer.initial.send_at, payload }, { headers });
      await axios.post(`${API_URL}/admin/emails/scheduled/${composer.initial.scheduled_id}/send-now`, {}, { headers });
      toast.success(`Email sent to ${payload.to.join(', ')}`);
    } else {
      const { data } = await axios.post(`${API_URL}/admin/emails/compose`, payload, { headers });
      toast.success(`Email sent to ${payload.to.join(', ')}${data.delivered > 1 ? ` (+${data.delivered - 1} copies)` : ''}`);
    }
    fetchEmails(); fetchDrafts(); fetchScheduled(); setRefreshKey((k) => k + 1);
  };
  const deleteDraft = async (d) => { try { await axios.delete(`${API_URL}/admin/email-drafts/${d.id}`, { headers }); fetchDrafts(); } catch { toast.error('Could not delete the draft'); } };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) return toast.error('Name and subject are required');
    try {
      if (editingTemplate) await axios.put(`${API_URL}/admin/email-templates/${editingTemplate.id}`, templateForm, { headers });
      else await axios.post(`${API_URL}/admin/email-templates`, templateForm, { headers });
      toast.success(editingTemplate ? 'Template updated' : 'Template created');
      setShowTemplateModal(false); setEditingTemplate(null); setTemplateForm({ name: '', subject: '', description: '', active: true }); fetchTemplates();
    } catch { toast.error('Failed to save template'); }
  };
  const toggleTemplateActive = async (t) => {
    try { await axios.put(`${API_URL}/admin/email-templates/${t.id}`, { active: !t.active }, { headers }); fetchTemplates(); toast.success(`Template ${!t.active ? 'enabled' : 'disabled'}`); } catch { toast.error('Failed to update template'); }
  };
  const sendLowStockAlert = async () => {
    try { const r = await axios.post(`${API_URL}/admin/send-low-stock-alert`, {}, { headers }); toast.success(r.data.message); fetchEmails(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send alert'); }
  };
  const retryEmail = async (logId) => {
    try { await axios.post(`${API_URL}/admin/email-logs/${logId}/retry`, {}, { headers }); toast.success('Email re-sent'); fetchEmails(); setRefreshKey((k) => k + 1); } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  const views = [['history', Mail, `Inbox (${emails.length})`, 'mails-view-inbox'], ['drafts', FileEdit, `Drafts (${drafts.length})`, 'mails-view-drafts'], ['scheduled', CalendarClock, `Scheduled (${scheduled.filter((i) => i.status === 'scheduled').length})`, 'mails-view-scheduled'], ['replies', MessageSquareReply, `Reply templates (${replyTemplates.length})`, 'mails-view-replies'], ['templates', FileText, `System templates (${templates.length})`, 'mails-view-templates']];

  return (
    <div data-testid="mails-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">Emails</h2>
          <p className="text-neutral-500 text-xs">Conversations with customers and the team — quotes, orders, invoices and replies in one place</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex bg-neutral-100 rounded-lg p-0.5">
            {views.map(([v, Icon, label, tid]) => (
              <button key={v} onClick={() => setActiveView(v)} className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${activeView === v ? 'bg-white shadow' : 'text-neutral-600'}`} data-testid={tid}><Icon className="w-3.5 h-3.5" /> {label}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={sendLowStockAlert} className="h-8 text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Low Stock Alert</Button>
          <Button size="sm" onClick={() => openComposer()} className="h-8 text-xs bg-amber-600 hover:bg-amber-700 rounded-full px-4" data-testid="mails-compose-btn"><Send className="w-3.5 h-3.5 mr-1.5" /> Compose</Button>
        </div>
      </div>

      {activeView === 'history' && (
        <EmailInbox emails={emails} loading={loading} token={token} onRefresh={fetchEmails} typeColor={getTemplateColor} onRetry={retryEmail} refreshKey={refreshKey}
          onReply={(init) => openComposer({ to: init.to, subject: init.subject, in_reply_to: init.in_reply_to, html: '' }, init.name ? `Reply to ${init.name}` : 'Reply')} />
      )}
      {activeView === 'drafts' && <DraftsList drafts={drafts} onDelete={deleteDraft} onOpen={(d) => openComposer({ to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, html: d.html, in_reply_to: d.in_reply_to, draft_id: d.id }, 'Draft')} />}
      {activeView === 'scheduled' && <ScheduledList items={scheduled} onEdit={editScheduled} onSendNow={sendNow} onCancel={cancelScheduled} busyId={busyId} />}
      {activeView === 'replies' && <ReplyTemplatesPanel token={token} templates={replyTemplates} onChanged={reloadReplyTemplates} />}
      {activeView === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-neutral-500">System templates are triggered automatically (quotes, orders, invoices, status updates).</p>
            <Button variant="outline" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', subject: '', description: '', active: true }); setShowTemplateModal(true); }}><Plus className="w-4 h-4 mr-2" /> Add Template</Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map((template) => (
              <div key={template.id} className={`bg-white border rounded-lg p-4 ${!template.active ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${getTemplateColor(template.name)}`}>{template.name.replace(/_/g, ' ')}</span>
                      {template.is_system && <span className="text-xs text-neutral-400">System</span>}
                    </div>
                    <h4 className="font-medium mt-2">{template.subject}</h4>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => toggleTemplateActive(template)} className={`p-1.5 rounded ${template.active ? 'hover:bg-amber-50' : 'hover:bg-green-50'}`} title={template.active ? 'Disable' : 'Enable'}>
                      {template.active ? <XCircle className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-green-500" />}
                    </button>
                    {!template.is_system && (
                      <button onClick={() => { setEditingTemplate(template); setTemplateForm({ name: template.name, subject: template.subject, description: template.description || '', active: template.active }); setShowTemplateModal(true); }} className="p-1.5 hover:bg-neutral-100 rounded"><Edit className="w-4 h-4 text-neutral-500" /></button>
                    )}
                  </div>
                </div>
                {template.description && <p className="text-sm text-neutral-500">{template.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <EmailComposer open={composer.open} onOpenChange={(o, meta) => { setComposer((c) => ({ ...c, open: o })); if (meta?.draftSaved || meta?.discarded) fetchDrafts(); }}
        token={token} initial={composer.initial} title={composer.title} onSend={sendComposed} onSchedule={scheduleComposed} agentName={agentName} />

      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Template Name *</Label><Input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g., welcome_email" className="mt-1" /></div>
            <div><Label>Subject Line *</Label><Input value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} placeholder="Email subject..." className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={templateForm.description} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} placeholder="What is this template for?" rows={2} className="mt-1" /></div>
            <div className="flex items-center gap-2"><input type="checkbox" checked={templateForm.active} onChange={(e) => setTemplateForm({ ...templateForm, active: e.target.checked })} className="rounded" /><Label className="text-sm">Active (emails will be sent)</Label></div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
            <Button onClick={saveTemplate} className="btn-primary">{editingTemplate ? 'Update' : 'Create'} Template</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MailsTab;
